import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Badge as BadgeIcon,
  CardMembership as CardIcon,
  InsertDriveFile as FileIcon,
  Add as AddIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import DocumentService from '../services/DocumentService';
import FilePreviewDialog, { isPreviewableFile } from './FilePreviewDialog';
import { usePostHog } from '@posthog/react';

const DOCUMENT_TYPES = [
  { value: 'id_photo', label: 'ID Photo', icon: <BadgeIcon /> },
  { value: 'white_card', label: 'White Card', icon: <CardIcon />, hasTicketNumber: true },
  { value: 'ticket_license_certificate', label: 'Ticket / License / Certificate', icon: <DocumentIcon />, hasTicketNumber: true, hasCustomLabel: true },
];

const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const DocumentUpload = ({ jobSeekerProfileId, readOnly = false }) => {
  const posthog = usePostHog();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);

  // Upload form state
  const [selectedType, setSelectedType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const documentService = new DocumentService();

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const docs = await documentService.getMyDocuments();
      // Exclude avatar documents from the general documents list
      const filtered = (docs || []).filter(d => d.documentType !== 'avatar');
      setDocuments(filtered);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (jobSeekerProfileId) {
      fetchDocuments();
    }
  }, [jobSeekerProfileId, fetchDocuments]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedType || !selectedFile || !jobSeekerProfileId) {
      setError('Please select a document type and file');
      return;
    }

    const typeConfig = DOCUMENT_TYPES.find(t => t.value === selectedType);
    if (typeConfig?.hasCustomLabel && !customLabel.trim()) {
      setError('Please provide a document name');
      return;
    }

    try {
      setUploading(true);
      setError('');

      await documentService.uploadDocument({
        jobSeekerProfileId,
        documentType: selectedType,
        documentLabel: typeConfig?.hasCustomLabel ? customLabel.trim() : typeConfig?.label,
        ticketNumber: ticketNumber.trim() || undefined,
        file: selectedFile,
      });

      setSuccess('Document uploaded successfully!');
      posthog?.capture('document_uploaded', { document_type: selectedType });
      setDialogOpen(false);
      resetForm();
      await fetchDocuments();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handlePreviewClick = async (doc) => {
    try {
      const { downloadUrl } = await documentService.getDownloadUrl(doc.id);
      setPreviewDocument({ ...doc, downloadUrl });
    } catch (err) {
      console.error('Preview error:', err);
      setError('Failed to load document preview');
    }
  };

  const handleDownloadClick = async (doc) => {
    try {
      const { downloadUrl } = await documentService.getDownloadUrl(doc.id);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download document');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      await documentService.deleteDocument(documentToDelete.id);
      setSuccess('Document deleted successfully');
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
      await fetchDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete document');
    }
  };

  const resetForm = () => {
    setSelectedType('');
    setCustomLabel('');
    setTicketNumber('');
    setSelectedFile(null);
  };

  const getDocumentIcon = (type) => {
    const config = DOCUMENT_TYPES.find(t => t.value === type);
    return config?.icon || <FileIcon />;
  };

  const selectedTypeConfig = DOCUMENT_TYPES.find(t => t.value === selectedType);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Document List */}
      {documents.length > 0 ? (
        <List>
          {documents.map((doc, index) => (
            <React.Fragment key={doc.id}>
              {index > 0 && <Divider />}
              <ListItem
                button
                onClick={() => handlePreviewClick(doc)}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {getDocumentIcon(doc.documentType)}
                </ListItemIcon>
                <ListItemText
                  primary={doc.documentLabel || doc.documentType}
                  slotProps={{
                    primary: { component: 'div' },
                    secondary: { component: 'div' },
                  }}
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {doc.fileName}
                      </Typography>
                      {doc.ticketNumber && (
                        <Chip label={`#${doc.ticketNumber}`} size="small" variant="outlined" color="info" />
                      )}
                    </Stack>
                  }
                />
                <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
                  {isPreviewableFile(doc.mimeType, doc.fileName) && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePreviewClick(doc); }}>
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownloadClick(doc); }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {!readOnly && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => { e.stopPropagation(); handleDeleteClick(doc); }}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No documents uploaded yet
        </Typography>
      )}

      {/* Upload Button */}
      {!readOnly && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': { borderColor: 'primary.dark', bgcolor: (t) => t.palette.mode === 'dark' ? 'hsla(145, 72%, 40%, 0.1)' : t.palette.primary[50] },
            }}
          >
            Upload Document
          </Button>
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Document Type */}
            <FormControl fullWidth>
              <InputLabel>Document Type *</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => { setSelectedType(e.target.value); setCustomLabel(''); setTicketNumber(''); }}
                label="Document Type *"
              >
                {DOCUMENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {type.icon}
                      <span>{type.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Label (for tickets/licenses) */}
            {selectedTypeConfig?.hasCustomLabel && (
              <TextField
                label="Document Name *"
                placeholder="e.g., Forklift License, First Aid Certificate"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                fullWidth
              />
            )}

            {/* Ticket Number */}
            {selectedTypeConfig?.hasTicketNumber && (
              <TextField
                label="Ticket / License Number"
                placeholder="Enter number"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                fullWidth
              />
            )}

            {/* File Upload */}
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                  borderColor: selectedFile ? 'primary.main' : 'divider',
                  color: selectedFile ? 'primary.main' : 'text.secondary',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                {selectedFile ? selectedFile.name : 'Choose File (JPEG, PNG, PDF - max 10MB)'}
                <input
                  type="file"
                  hidden
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !selectedType || !selectedFile}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{documentToDelete?.documentLabel || documentToDelete?.fileName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <FilePreviewDialog
        open={Boolean(previewDocument)}
        onClose={() => setPreviewDocument(null)}
        title={previewDocument?.documentLabel || previewDocument?.fileName}
        fileName={previewDocument?.fileName}
        mimeType={previewDocument?.mimeType}
        downloadUrl={previewDocument?.downloadUrl}
      />
    </Box>
  );
};

export default DocumentUpload;
